import { useQuery } from '@tanstack/react-query';
import type { ApiException } from '@/lib/api/types';
import * as paperApi from './paperApi';
import type { BandStatus, BandVariant } from './bandTypes';
import type { PaperComparison, PaperOverview, PaperProfile } from './paperTypes';
import type { SwingAccountKey, SwingAccountTab, SwingStatus } from './swingTypes';
import type { TradingProfile } from './types';

/**
 * AI 모의투자 조회 훅. (18단계)
 *
 * 🔴 갱신 주기를 관심종목 시세(15초)와 다르게 잡는다
 *   모의투자 계좌는 체크포인트에서만 움직인다 — 하루 세 번이다.
 *   15초마다 다시 읽어도 같은 값이고, 응답이 무겁다
 *   (계좌 + 곡선 + 보유 + 청산 + 회고를 한 번에 준다).
 *
 *   다만 장중 평가액은 현재가 기준이라 조금씩 움직인다.
 *   2분이면 그 움직임을 놓치지 않으면서 부하가 없다.
 */

/** 2분. 체크포인트 사이의 평가액 변화를 따라갈 정도다. */
const STALE_TIME = 2 * 60 * 1000;

export function usePaperOverview(profile: PaperProfile) {
  return useQuery<PaperOverview, ApiException>({
    queryKey: ['trading', 'paper', profile],
    queryFn: () => paperApi.fetchPaperOverview(profile),
    staleTime: STALE_TIME,
    refetchInterval: STALE_TIME,
    // 탭이 뒤에 있을 때는 멈춘다. 화면을 안 보는 동안 부를 이유가 없다.
    refetchIntervalInBackground: false,
  });
}

export function usePaperComparison() {
  return useQuery<PaperComparison, ApiException>({
    queryKey: ['trading', 'paper', 'comparison'],
    queryFn: () => paperApi.fetchPaperComparison(),
    staleTime: STALE_TIME,
    refetchInterval: STALE_TIME,
    refetchIntervalInBackground: false,
  });
}

/**
 * 지금 회차에 쉬는 추천 프로필 (2026-10-10 사용자 결정 · 141번 10.6 — 분할익절 · 하이리스크 잠정 중단).
 *
 * 🔴 판정은 서버가 한다 — 매매 · 회고 · 선별 · 비교와 같은 PaperActiveProfiles. 화면이 회차 번호로 따로 셈하지 않는다.
 * 🔴 못 받으면(불러오는 중 · 오류) 빈 목록으로 본다 = 탭을 숨기지 않는다. 모르면 지금처럼 보이는 쪽이 안전하다.
 * 바뀌는 때는 회차를 여는 순간뿐이라 오래 둔다(10분).
 */
export function useRestingProfiles(): TradingProfile[] {
  const { data } = useQuery<TradingProfile[], ApiException>({
    queryKey: ['trading', 'paper', 'resting'],
    queryFn: () => paperApi.fetchRestingProfiles(),
    staleTime: 10 * 60 * 1000,
  });
  return data ?? [];
}

/**
 * 밴드 매매 탭. 갱신 주기는 다른 모의투자 탭과 같다.
 *
 * @param variant 계좌 변형. 없으면 보수다. 🔴 캐시 키에 들어가야 두 탭이
 *                서로의 데이터를 보여주지 않는다 (V119)
 */
/**
 * 화면에 보일 국내 스윙 계좌 탭 (2026-10-10 · 141번 12.6-5). 4회차 전이면 빈 목록 — 탭이 안 생긴다.
 * 🔴 못 받으면 빈 목록 = 탭을 안 그린다(없는 계좌 탭을 그리는 것보다 안 보이는 쪽이 낫다). 바뀌는 때는 회차를 여는 순간뿐이라 10분
 */
export function useSwingAccounts(country: 'KR' | 'US' = 'KR'): SwingAccountTab[] {
  const { data } = useQuery<SwingAccountTab[], ApiException>({
    queryKey: ['trading', 'swing', 'accounts', country],
    queryFn: () => paperApi.fetchSwingAccounts(country),
    staleTime: 10 * 60 * 1000,
  });
  return data ?? [];
}

/**
 * 국내 스윙 계좌 탭. 갱신 주기는 다른 모의투자 탭과 같다(2분).
 *
 * @param account null 이면 부르지 않는다(도움말 모달이 스윙 탭이 아닐 때)
 */
export function useSwingStatus(account: SwingAccountKey | null) {
  return useQuery<SwingStatus, ApiException>({
    queryKey: ['trading', 'swing', account],
    // 근거: enabled 가 account !== null 일 때만 queryFn 을 부른다 — 여기서 account 는 null 이 아니다
    queryFn: () => paperApi.fetchSwingStatus(account as SwingAccountKey),
    enabled: account !== null,
    staleTime: STALE_TIME,
    refetchInterval: STALE_TIME,
    refetchIntervalInBackground: false,
  });
}

export function useBandStatus(variant?: BandVariant) {
  return useQuery<BandStatus, ApiException>({
    queryKey: ['trading', 'band', variant ?? 'CONSERVATIVE'],
    queryFn: () => paperApi.fetchBandStatus(variant),
    staleTime: STALE_TIME,
    refetchInterval: STALE_TIME,
    refetchIntervalInBackground: false,
  });
}
